import React, { useMemo } from 'react';
import { Box, Paper, Collapse } from '@mui/material';
import { useEventCardExpand } from './hooks/useEventCardExpand';
import { useSessionTimelineTheme } from './theme';
import { getSessionIcon } from './lib/iconMapping';
import { EventCardHeader } from './components/EventCardHeader';
import { EventCardContent } from './components/EventCardContent';
import { EventCardActions } from './components/EventCardActions';
import { EventCardRelatedEvents } from './components/EventCardRelatedEvents';
import { TokenizedValue } from './components/TokenizedValue';
import { hasTokens } from '@/shared/lib/sessionTokens/parseTokens';
import type {
  EventCardProps,
  EventField,
  EventAction,
  ContentData,
  CustomComponentData,
} from './types/sessionTimeline.types';

/**
 * ContentData를 ReactNode로 변환
 */
function transformContent(content: ContentData): React.ReactNode {
  switch (content.type) {
    case 'text':
      return hasTokens(content.value)
        ? React.createElement(TokenizedValue, { value: content.value })
        : content.value;
    case 'html':
      // HTML은 dangerouslySetInnerHTML로 렌더링 (주의 필요)
      return React.createElement('div', {
        dangerouslySetInnerHTML: { __html: content.value },
      });
    case 'component':
      // 컴포넌트 타입은 클라이언트에서 처리해야 함
      // 여기서는 기본적으로 텍스트로 반환
      console.warn(
        `Component type "${content.componentType}" is not supported. Rendering as text.`
      );
      return content.value;
    default:
      return content.value;
  }
}

const isHistoryAction = (id: string) => id.startsWith('internal:') && id.includes('history');

/** 세션 데이터가 동일한지 비교 (리렌더 스킵용) */
function areSessionPropsEqual(prev: EventCardProps, next: EventCardProps): boolean {
  if (prev.sessionId !== next.sessionId) return false;
  if (prev.title !== next.title) return false;
  if (prev.occurredAt !== next.occurredAt) return false;
  if (prev.isLatestSession !== next.isLatestSession) return false;
  if (prev.index !== next.index) return false;
  if (prev.relatedEventsCount !== next.relatedEventsCount) return false;
  if (prev.defaultExpandedState !== next.defaultExpandedState) return false;
  if (prev.historyActionId !== next.historyActionId) return false;
  if (prev.previousOccurredAt !== next.previousOccurredAt) return false;
  if (JSON.stringify(prev.content) !== JSON.stringify(next.content)) return false;
  if (JSON.stringify(prev.fields) !== JSON.stringify(next.fields)) return false;
  if (JSON.stringify(prev.actions) !== JSON.stringify(next.actions)) return false;
  if (JSON.stringify(prev.customComponent) !== JSON.stringify(next.customComponent)) return false;
  if (JSON.stringify(prev.fieldHistory) !== JSON.stringify(next.fieldHistory)) return false;
  if (JSON.stringify(prev.relatedEvents) !== JSON.stringify(next.relatedEvents)) return false;
  return true;
}

const EventCardInner = function EventCard({
  sessionId,
  title,
  occurredAt,
  sessionIconType,
  sessionIcon: providedSessionIcon,
  index,
  content,
  fields,
  actions,
  fieldHistory,
  historyActionId,
  relatedEvents,
  relatedEventsCount,
  collapsible = true,
  defaultExpandedState = 'content',
  previousOccurredAt,
  isLatestSession = false,
  onActionClick,
  onRelatedEventsClick,
  onExpandStateChange,
  fieldIcons,
  customComponent,
  customComponentMap,
}: EventCardProps) {
  const { getCardSx } = useSessionTimelineTheme();

  const {
    expandState,
    toggleExpand,
    toggleTimeline,
    isCollapsed,
    isContentExpanded,
    isTimelineExpanded,
  } = useEventCardExpand({
    defaultExpandedState,
    collapsible,
    onExpandStateChange,
    sessionId,
  });

  // 세션 아이콘 처리
  const sessionIcon = useMemo(() => {
    if (providedSessionIcon) {
      return providedSessionIcon;
    }
    if (sessionIconType) {
      const IconComponent = getSessionIcon(sessionIconType);
      if (IconComponent) {
        return React.createElement(IconComponent);
      }
    }
    return undefined;
  }, [providedSessionIcon, sessionIconType]);

  // 필드 변환 (EventFieldData를 EventField로 변환)
  const transformedFields = useMemo<readonly EventField[] | undefined>(() => {
    if (!fields) return undefined;

    return fields.map((field) => {
      // 이미 EventField인 경우
      if (
        'value' in field &&
        (typeof field.value === 'string' || React.isValidElement(field.value))
      ) {
        return field as EventField;
      }

      // EventFieldData인 경우 변환
      const fieldData = field as any;
      const valueString =
        typeof fieldData.value === 'string' ? fieldData.value : String(fieldData.value);

      let icon: React.ReactNode | undefined;
      if (fieldData.iconType) {
        if (fieldIcons && fieldIcons[fieldData.iconType]) {
          icon = fieldIcons[fieldData.iconType];
        } else {
          const IconComponent = getSessionIcon(fieldData.iconType);
          if (IconComponent) {
            icon = React.createElement(IconComponent);
          }
        }
      }

      return {
        label: fieldData.label,
        value: valueString,
        icon,
        copyable: fieldData.copyable,
        multiline: fieldData.multiline,
        markdown: fieldData.markdown,
      } as EventField;
    });
  }, [fields, fieldIcons]);

  // 액션 변환 (EventActionData를 EventAction으로 변환)
  const transformedActions = useMemo<readonly EventAction[] | undefined>(() => {
    if (!actions) return undefined;

    return actions.map((action) => {
      // 이미 EventAction인 경우
      if ('onClick' in action || (typeof action === 'object' && 'default' in action)) {
        return action as EventAction;
      }

      // EventActionData인 경우 변환
      const actionData = action as any;
      return {
        id: actionData.id,
        label: actionData.label,
        color: actionData.color,
        default: actionData.default,
      } as EventAction;
    });
  }, [actions]);

  // 컨텐츠 변환 (ContentData를 ReactNode로 변환)
  const transformedContent = useMemo<React.ReactNode | undefined>(() => {
    if (!content) return undefined;

    // 이미 ReactNode인 경우
    if (
      React.isValidElement(content) ||
      typeof content === 'string' ||
      typeof content === 'number'
    ) {
      return content;
    }

    // ContentData 객체인 경우 변환
    if (typeof content === 'object' && 'type' in content && 'value' in content) {
      return transformContent(content as ContentData);
    }

    return content;
  }, [content]);

  // 커스텀 컴포넌트 변환
  const renderedCustomComponent = useMemo<React.ReactNode | undefined>(() => {
    if (!customComponent) return undefined;

    // 이미 ReactNode인 경우
    if (React.isValidElement(customComponent)) {
      return customComponent;
    }

    // CustomComponentData 객체인 경우 변환
    if (typeof customComponent === 'object' && 'componentType' in customComponent) {
      const componentData = customComponent as CustomComponentData;

      // customComponentMap에서 컴포넌트 찾기
      if (customComponentMap && customComponentMap[componentData.componentType]) {
        const Component = customComponentMap[componentData.componentType];
        return React.createElement(Component, componentData.props || {});
      }

      // 매핑이 없으면 경고
      console.warn(
        `Custom component type "${componentData.componentType}" is not found in customComponentMap.`
      );
      return null;
    }

    return null;
  }, [customComponent, customComponentMap]);

  return (
    <Paper sx={getCardSx()}>
      <EventCardHeader
        sessionIcon={sessionIcon}
        title={title}
        index={index}
        occurredAt={occurredAt}
        expandState={expandState}
        collapsible={collapsible}
        onToggleExpand={toggleExpand}
        previousOccurredAt={previousOccurredAt}
        isLatestSession={isLatestSession}
      />

      <Collapse
        in={!isCollapsed}
        timeout={300}
        easing={{
          enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
          exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Box>
          {/* 커스텀 컴포넌트가 있으면 우선 표시 */}
          {renderedCustomComponent && <Box sx={{ marginTop: 2 }}>{renderedCustomComponent}</Box>}
          {/* 커스텀 컴포넌트가 없으면 기본 content/fields 표시 */}
          {!renderedCustomComponent && (
            <EventCardContent
              content={transformedContent}
              fields={transformedFields}
              fieldHistory={fieldHistory}
              historyActionId={historyActionId}
              sessionId={sessionId}
              onActionClick={onActionClick}
            />
          )}
          <EventCardActions
            actions={transformedActions?.filter((a) => !isHistoryAction(a.id))}
            sessionId={sessionId}
            onActionClick={onActionClick}
          />
          {(isContentExpanded || isTimelineExpanded) && (
            <EventCardRelatedEvents
              relatedEvents={relatedEvents}
              relatedEventsCount={relatedEventsCount}
              expandState={expandState}
              sessionId={sessionId}
              onToggleTimeline={toggleTimeline}
              onRelatedEventsClick={onRelatedEventsClick}
              customComponentMap={customComponentMap}
            />
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export const EventCard = React.memo(EventCardInner, areSessionPropsEqual);
